import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import { initI18n, trn } from "./shared/i18n";
import { readRuntimeLocalizationConfig } from "./runtime-localization";
import { loadCurrentDomainRuntime } from "./domain-runtime";
import { refreshBootstrap } from "./runtime-bootstrap";
import { resolveConsumerRuntimeConnectionUuid } from "./runtime-endpoints";
import { waitForTerminalThemeAssets } from "./terminal-theme";

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("❌ No se encontró el elemento #root en el DOM");
}

const root = createRoot(rootElement);

function StartupScreen({ localized = false }: { localized?: boolean }) {
  const title = localized
    ? trn("app.consumer.startup.preparing", "Preparing the application")
    : "Loading app…";
  const detail = localized
    ? trn("app.consumer.startup.loading_resources", "Loading configuration and resources")
    : null;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--dq-terminal-background, #111827)",
      }}
    >
      <div
        style={{
          display: "grid",
          justifyItems: "center",
          gap: 22,
          color: "#f8fafc",
          fontFamily: "system-ui, sans-serif",
        }}
        role="status"
        aria-live="polite"
      >
        <div className="consumer-runtime-loader" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div style={{ display: "grid", gap: 6, textAlign: "center" }}>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 750 }}>{title}</p>
          {detail ? (
            <p style={{ margin: 0, color: "rgba(248,250,252,0.68)", fontSize: 14 }}>
              {detail}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DomainConfigurationError() {
  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 32,
        background: "#111827",
        color: "#f8fafc",
        fontFamily: "system-ui, sans-serif",
        textAlign: "center",
      }}
    >
      <section style={{ maxWidth: 560 }}>
        <h1 style={{ margin: "0 0 16px", fontSize: 36 }}>Kiosco no configurado</h1>
        <p style={{ margin: 0, color: "#cbd5e1", fontSize: 18, lineHeight: 1.55 }}>
          Este dominio todavía no tiene una configuración publicada. Contacta con el administrador de la plataforma.
        </p>
      </section>
    </main>
  );
}

const spinnerStyle = document.createElement("style");
spinnerStyle.textContent = `
@keyframes consumer-runtime-loader-pulse {
  0%, 100% { transform: scaleY(.45); opacity: .42; }
  50% { transform: scaleY(1); opacity: 1; }
}
.consumer-runtime-loader {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 48px;
}
.consumer-runtime-loader span {
  width: 7px;
  height: 36px;
  border-radius: 999px;
  background: var(--dq-button-bg, #f59e0b);
  transform-origin: center;
  animation: consumer-runtime-loader-pulse .9s ease-in-out infinite;
}
.consumer-runtime-loader span:nth-child(2) { animation-delay: .12s; }
.consumer-runtime-loader span:nth-child(3) { animation-delay: .24s; }
@media (prefers-reduced-motion: reduce) {
  .consumer-runtime-loader span { animation: none; opacity: .82; transform: scaleY(.72); }
}
`;
document.head.appendChild(spinnerStyle);

const waitForImage = (url: string): Promise<void> => {
  const source = String(url || "").trim();
  if (!source) return Promise.resolve();
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => resolve();
    image.src = source;
    if (image.complete) resolve();
  });
};

const waitForCriticalImages = async (timeoutMs = 4_000): Promise<void> => {
  const urls = [
    localStorage.getItem("restaurantLogo") || "",
    localStorage.getItem("marketplaceBackgroundImage") || "",
  ];
  await Promise.race([
    Promise.all(urls.map(waitForImage)),
    new Promise<void>((resolve) => window.setTimeout(resolve, timeoutMs)),
  ]);
};

root.render(<StartupScreen />);

void (async () => {
  try {
    await loadCurrentDomainRuntime();
  } catch (error) {
    console.error("Consumer runtime domain resolution failed", error);
    root.render(<DomainConfigurationError />);
    return;
  }
  const runtimeLocalization = readRuntimeLocalizationConfig();
  await initI18n({
    supportedLanguages: runtimeLocalization.availableLanguages,
    defaultLanguage: runtimeLocalization.defaultLanguage,
  });
  root.render(<StartupScreen localized />);
  const connectionUuid = resolveConsumerRuntimeConnectionUuid();
  if (connectionUuid && navigator.onLine) {
    await refreshBootstrap(connectionUuid, {
      language: localStorage.getItem("volqan.consumer.language") || runtimeLocalization.defaultLanguage || "es",
      refreshPlatformBranding: true,
      pageTitle: "Kiosk",
    }).catch((error) => {
      console.warn("Consumer runtime initial bootstrap failed; using published snapshot", error);
    });
  }
  await Promise.all([
    waitForTerminalThemeAssets(),
    waitForCriticalImages(),
  ]);
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
})();
